<ng-container *transloco="let convert;">
  @if (!waitingResponse && !successfulResponse && intialOffer) {
    <app-offer-modals-header
      heading="{{ convert('vehicles.initialOffer.textMakeOffer') }}"
      [tenantName]="tenantName"
      [headingId]="modalId + '-title'"
    ></app-offer-modals-header>
    <p class="text-danger fw-bold" [class.d-none]="!errorMessage" aria-atomic="true" aria-live="polite" #error>
      @if (errorMessage) {
        {{ convert('vehicles.followUpOffer.errorMsgTitle') }}
      }
    </p>
    @if (showWarning) {
      <app-vehicle-changed-warning></app-vehicle-changed-warning>
    }
    <div class="modal__content">
      <!--
        The comp's vehicle identification block (Figma 10360:9077): the title in bold with the VIN on
        the line beneath it, between the 'Make Offer' heading and the breakdown. Same block, same
        markup and the same reasoning as Buy Now's and the bid modal's.

        It is gated on the flag rather than rendered unconditionally because flag-off tenants already
        name the vehicle -- their #disclosures prose below reads '... purchase the 2024 Jeep Wagoneer
        L Series III - VIN 1C4SJ... at this price' -- so rendering this as well would state it twice.
        The all-in binding sentence that replaces that prose names neither, which is what QA raised
        and what the comp answers by lifting both out to here.

        The VIN keeps .stockwave-bid-buy-vin. Stockwave reads the VIN out of that hook and it existed
        only in the flag-off prose, so with the flag on this modal exposed no VIN for the extension to
        find; this restores it.
      -->
      @if (showDripBreakdown) {
        <p class="make_offer_vehicle">
          <span class="make_offer_vehicle__title">{{ item.titleSimple }}</span>
          <span class="make_offer_vehicle__vin">{{ convert('common.textVin') }} <span class="stockwave-bid-buy-vin">{{ item.vin }}</span></span>
        </p>
      }
      <!--
        [showSummary]="!showDripBreakdown" drops the VIN / DATE / AMOUNT / STATUS strip for all-in
        users, per the comps (Figma 10360:9077, 10493:16256), which carry no strip. With the block above
        now naming the vehicle and the breakdown below quoting the money, the strip restated both -- and
        its AMOUNT is the offer as it stands, against the breakdown's offer being made, so the two
        read as a contradiction rather than a repetition.

        The offer CHAIN stays: it is the thing this modal exists to show, and only the strip is absent
        from the comps. Not collapsed either, unlike View Offer's -- that treatment has its own comp
        (10493:16426) and these frames do not ask for it.
      -->
      <app-offer-history [vin]="item.vin" [offerHistory]="offerHistory" [showSummary]="!showDripBreakdown"></app-offer-history>
      <!--
        M4.13: flag OFF only. With all-in pricing on, ONE paragraph below the breakdown replaces
        every block in here -- see the drip block inside the form. This whole div is gated rather
        than rewritten in place because the comp puts the prose AFTER the price breakdown, and the
        breakdown lives inside the <form> below while these blocks sit above it. #disclosures is
        referenced nowhere -- no styles, no tests, no aria -- so removing it with the flag on is
        safe. Flag off, everything below renders exactly as today, in today's order.
      -->
      @if (!showDripBreakdown) {
        <div id="disclosures">
          <p data-test-id="app-follow-up-offer-e-modal-info-text">
            <strong>{{followUpOfferTranslations.msgDescriptionImportant}}</strong>
            {{followUpOfferTranslations.msgDescriptionBySelecting}}
            <strong>{{followUpOfferTranslations.msgDescriptionMakeOffer}}</strong>
            {{followUpOfferTranslations.msgDescriptionPartOne}}
            {{vehicleTitle}}<span class="stockwave-bid-buy-vin">{{item.vin}}</span>{{followUpOfferTranslations.msgDescriptionPartTwo}}
          </p>
          <!--
            No gate: showTransportationParagraph was `flag on ? is a fee quoted : true`, and this is
            the flag-off branch, so it was always true here. The flag-on gate is
            showTransportationDisclosure, on the drip paragraph inside the form.
          -->
          <p data-test-id="app-follow-up-offer-e-modal-transportation-text">
            {{followUpOfferTranslations.msgDescriptionTransportation}}
          </p>
          <ng-container *ngTemplateOutlet="financeSourceFeeMessage"></ng-container>
          @if (showSellingDisclosure) {
            <p>
              {{ convert('vehicles.followUpOffer.showSellingDisclousure') }}
              <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container>.
              </p>
            }
          </div>
      }
        <form name="makeOfferForm" [formGroup]="form" (ngSubmit)="$event.preventDefault(); newOffer()" novalidate>
          <div class="row">
            <ng-container *ngTemplateOutlet="OfferAmountOrBreakdown"></ng-container>
            <!--
              M4.13, flag ON: the comps (Figma 10493:16256) mark today's "Important: By selecting
              Make Offer ..." paragraph hidden and put ONE sentence in its place, below the price
              breakdown, with the Selling Disclosures acceptance folded into it and the
              transportation sentence as its own paragraph.

              The quoted figure is the breakdown's own total, transportation INCLUDED -- see
              offerQuotedPriceDisplay. This reverses M4.4's note that the prose should stay as it
              is: that was written when no comp for the all-in wording existed, and the reasoning
              was that for an offer the fees genuinely ARE added on top of the amount the buyer
              names, so today's sentence is not the misstatement it is under Buy Now. True, but
              the comp answers a different question -- what the buyer is bound to -- and answers
              it with the total they can see directly above.

              The data-test-ids are deliberately the SAME as the flag-off paragraphs' rather than
              new ones: only one branch ever renders, and the automation suite looks for the info
              text and the transportation text by those names regardless of which copy is in place.
            -->
            @if (showDripBreakdown) {
              <div class="col-12">
                <p id="mom_binding_text" data-test-id="app-follow-up-offer-e-modal-info-text">
                  <span [innerHTML]="convert('vehicles.followUpOffer.msgBindingOfferAllInPartOne')"></span>
                  <!--
                    Split around the link rather than embedding an anchor in the translation string:
                    the href is config-driven and routerLink cannot be applied through [innerHTML].

                    No whitespace between the outlet and part two -- part two opens with '.', and a
                    newline there would render 'Selling Disclosures .'
                  -->
                  <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container><span
                    [innerHTML]="convert('vehicles.followUpOffer.msgBindingOfferAllInPartTwo', { amount: offerQuotedPriceDisplay })"></span>
                </p>
                @if (showTransportationDisclosure) {
                  <p id="mom_transportation_text" data-test-id="app-follow-up-offer-e-modal-transportation-text"
                    [innerHTML]="convert('vehicles.followUpOffer.msgTransportationOptional')"></p>
                }
                <ng-container *ngTemplateOutlet="financeSourceFeeMessage"></ng-container>
              </div>
            }
            @if (errorMessage) {
              <div class="col-12 errorBox" aria-atomic="true" aria-live="polite">
                @if (errorMessage) {
                  <p class="text-danger fw-bold" id="follow-up-offer-error" #error role="alert">
                    {{errorMessage === BuyErrors.MSG_BUY_003 ? errorMessage : convert('error.' + errorMessage)}}
                  </p>
                }
              </div>
            }
          </div>
          <div class="d-flex flex-column app-textarea form-group mb-0">
            <div class="form-group">
              <!--
                The comp (Figma 10360:9108) labels this field for what it is -- a message to the seller
                -- rather than 'COMMENTS (OPTIONAL)', and gives the box a 'Comment' placeholder.
                Flag-off keeps today's label, so the change lands only where the comp applies.

                followUpOffer.labelSellerQuestion, not the sellerUpdate key with the same name: the comp
                reads 'ASK SELLER QUESTION:' where seller-updates asks 'Ask seller a question:', so
                borrowing that value and re-casing it would have retitled a panel with no comp. Its own
                key under this modal's namespace is the smaller change -- the same reasoning, and the
                same shape, as textOfferHistory vs textHistory in offer-history.

                Uppercase at source, like labelComment and labelOfferAmount beside it, so no CSS
                text-transform is needed. French keeps its article: 'Posez une question au vendeur' has
                no 'a' to drop and 'Posez question' is not French.

                The placeholder is an existing key reused as-is -- the same word in the same kind of box
                on edit-comment -- so there is nothing new to translate for it.
              -->
              <label class="d-block text-start" for="mom_comments_textbox">{{
                showDripBreakdown
                  ? convert('vehicles.followUpOffer.labelSellerQuestion')
                  : convert('vehicles.followUpOffer.labelComment')
              }}</label>
              <textarea
                id="mom_comments_textbox"
                class="form-control"
                rows="3"
                maxlength="150"
                [attr.placeholder]="showDripBreakdown ? convert('shared.editComment.placeholderComment') : null"
                formControlName="offerComment"
                appCharacterCount
                (keyup)="replaceInvalidCharacters($event)"
                #comment="appCharacterCount"
              ></textarea>
              <app-character-count
                [count]="comment.count$ | async"
                [maxLength]="150"
                [characterCountTranslate]="characterCountData">
              </app-character-count>
            </div>
          </div>
          <div class="d-flex flex-column flex-lg-row lg-justify-content-start gap-3">
            <button
              id="mom_make_offer_button"
              class="btn btn-primary stockwave-bid-button"
              type="submit"
              data-track-elem="button"
              data-track-name="makeBuyAction"
              >
              {{ convert('vehicles.followUpOffer.btnMakeOffer') }}
            </button>
            <button
              id="mom_cancel_button"
              class="ms-lg-2 mt-3 mt-lg-0 btn btn-link"
              type="button"
              (click)="cancel()"
              data-track-elem="button"
              data-track-name="Cancel"
              data-track-trigger="close"
              >
              <span>{{ convert('common.btnCancel') }}</span>
            </button>
          </div>
        </form>
      </div>
      <app-modal-close (close)="cancel()" [identifier]="'vom_close_x_button'"></app-modal-close>
    }

    @if (!waitingResponse && !successfulResponse && !intialOffer) {
      <app-higher-existing-offer
        #HigherExistingOfferComponent
        [offerAmount]="form.get('offerAmount').value"
        [highestOfferAmount]="highestOfferAmount"
        (continueOffer)="continue()"
        (cancelOffer)="cancel()"
        (backOffer)="back()"
      ></app-higher-existing-offer>
    }

    @if (waitingResponse && !successfulResponse) {
      <!-- submitting -->
      <app-action-submitting actionText="{{ convert('vehicles.followUpOffer.actionTextSubmittingOffer') }}"></app-action-submitting>
    }

    @if (!waitingResponse && successfulResponse) {
      <!-- success -->
      <app-action-submitted (close)="cancel()" actionText="{{ convert('vehicles.followUpOffer.actionTextOfferSubmitted') }}" [tenantName]="tenantName">
      </app-action-submitted>
    }

    <!--
      The Selling Disclosures link, extracted in M4.13 so today's disclosure paragraph and the new
      binding sentence share ONE definition of it instead of two that can drift apart. The markup
      inside is the existing anchor verbatim, attributes and all.

      The @else branch is not padding. showSellingDisclosure gates the disclosuresLinkEnabled flag,
      and with that flag off the binding sentence still has to read "... and you accept the Selling
      Disclosures." -- the words stay, only the anchor goes. Without it part one would end
      mid-phrase on "the". The flag is named for the LINK, so dropping the link and keeping the
      noun is what it asks for. The legacy paragraph keeps its own outer @if, so nothing renders
      there either way.
    -->
    <ng-template #SellingDisclosuresLink>
      @if (showSellingDisclosure) {
        <strong
          ><a
          target="_blank"
          rel="opener"
          routerLink="{{ sellingDisclosureUrl }}"
          data-track-elem="button"
          data-track-name="Selling Disclosures"
          [attr.aria-label]="convert('vehicles.followUpOffer.areaLabelSellingDisclosure')"
          >{{ convert('vehicles.followUpOffer.textSellingDisclosures') }}</a
          ></strong
          >
      } @else {
        <strong>{{ convert('vehicles.followUpOffer.textSellingDisclosures') }}</strong>
      }
    </ng-template>

    <ng-template #financeSourceFeeMessage>
      @if (isFinanceSourceFeeIncluded()) {
        <p aria-labelledby="bid-modal-finance-source-fee-text" [innerHTML]="convert('vehicles.bidModal.textFinanceSourceFee',{name: financeSource.financeSourceName, amount: financeSource.feeAmount})"></p>
      }
    </ng-template>

    <!--
      The currency field itself, with no label of its own, so the two hosts below can each supply the
      labelling their layout needs. Extracted rather than copied: it is the only place the buyer names
      the amount their offer binds them to, and two copies drifting -- a mask, a separator, an id --
      is a money defect. offerAmountInputConfig carries whichever labelledby the rendered layout has.
    -->
    <ng-template #OfferAmountInput>
      <app-currency-input
        [inputConfig]="offerAmountInputConfig"
        [inputForm]="form"
        [inputId]="'make_offer_modal_input'"
      ></app-currency-input>
    </ng-template>

    <!--
      Today's amount block: its own label above the field. Used whenever the all-in breakdown is not
      rendering, which is every flag-off case.
    -->
    <ng-template #OfferAmount>
      <div class="col-6">
        <div class="form-group mb-4 mb-sm-3">
          <label for="make_offer_modal_input" id="follow-up-offer-label">{{ convert('vehicles.followUpOffer.labelOfferAmount') }}</label>
          <ng-container *ngTemplateOutlet="OfferAmountInput"></ng-container>
        </div>
      </div>
    </ng-template>

    <!--
      All-in pricing (M4.6), Pattern B. The field moves inside the breakdown, labelled by the
      breakdown's own base-amount label -- which is why the <label> above is not rendered here too --
      with the required fees and optional transportation itemised beneath it and the total updating as
      the buyer types.

      The projected wrapper must carry appModalPriceBreakdownAmount: content matching no ng-content
      selector is discarded silently rather than rendered, so the field would simply vanish without it.
    -->
    <ng-template #AllInBreakdown>
      <div class="col-12 mb-4 mb-sm-3">
        <!--
          [showCents]="false" per the comp (Figma 10360:9077), which quotes OFFER AMOUNT $50,000,
          SERVICE FEE $400, TRANSPORTATION FEE $350 and TOTAL PURCHASE PRICE $50,750 -- whole dollars
          in every row. Paired with offerQuotedPriceDisplay's withCents: false, and the two must move
          together: the binding sentence quotes the same total this block's last row shows, so a
          mismatch writes one figure two ways inside one modal.

          Bound here rather than flipping ModalPriceBreakdownComponent's default, which stays
          input(true) for the legacy BidModalComponent. Same call as View Offer's.
        -->
        <app-modal-price-breakdown
          [idPrefix]="dripIdPrefix"
          [pattern]="additivePattern"
          [baseAmount]="dripOfferBaseAmount"
          [feeRows]="dripFeeRows"
          [transportationFee]="dripTransportationFee"
          [showCents]="false"
          [translations]="dripBreakdownTranslations"
          [useProjectedAmount]="true"
          [inlineControlOnNarrow]="true">
          <span appModalPriceBreakdownAmount class="offer-amount-breakdown-control">
            <ng-container *ngTemplateOutlet="OfferAmountInput"></ng-container>
          </span>
        </app-modal-price-breakdown>
      </div>
    </ng-template>

    <ng-template #OfferAmountOrBreakdown>
      @if (showDripBreakdown) {
        <ng-container *ngTemplateOutlet="AllInBreakdown"></ng-container>
      } @else {
        <ng-container *ngTemplateOutlet="OfferAmount"></ng-container>
      }
    </ng-template>

  </ng-container>
